import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import clsx from 'clsx';

import {
  Badge,
  badgeStyle,
  badgeType,
  colors,
  fontWeight,
  Heading,
  headingLevel,
  iconBtnType,
  IconButton,
  Icons,
} from '../../../../../packages';

import { useDomainType } from '../../../../../hooks/useDomainType';
import { profileActionCreators } from '../../../../../redux/modules/profile';
import { getProfile } from '../../../../../redux/modules/profile/selectors';
import { createNewAssociatedTag } from '../../../../../services/member/member.service';
import { getAuth } from '../../../../../redux/modules/auth/selectors';
import { DomainTypesEnum } from '../../../../../constants/CommonConstants';

import PriorityEmpty from '../PriorityEmpty';
import SideEffectCard from './components/side-effect-card';
import SideEffectDetail from './components/side-effect-detail';
import SideEffectEdit from './components/side-effect-editing';
import AddNewReport from '../AddNewReport';

import { stepTitles } from './SideEffects.constants';
import { useStyles } from '../../MemberDetail.styles';

const SideEffects = () => {
  const classes = useStyles({});
  const dispatch = useDispatch();
  const { memberId } = useParams();
  const { meta } = useSelector(getAuth);
  const { profile, selectedPatient } = useSelector(getProfile);
  const [isLoading, domainData, domainOptions] = useDomainType({
    type: DomainTypesEnum.SIDE_EFFECTS,
  });
  const [showAddItem, setShowAddItem] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [errorApi, setError] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [isEditing, setIsEditing] = useState(false);

  const onAddNewSideEffects = () => {
    setShowAddItem(true);
  };

  const onSelectedItemClick = (item, isEdit = false) => {
    setSelectedItem(item);
    setIsEditing(isEdit);
  };

  const onCloseDrawer = () => {
    setSelectedItem(null);
    setIsEditing(false);
    setShowAddItem(false);
  };

  const onClickNewReport = () => {
    setShowAddItem(true);
  };

  const onUpdateItem = async data => {
    setIsSaving(true);
    setError(null);
    try {
      await createNewAssociatedTag({
        patientId: memberId,
        tagItems: [
          {
            assignedBy: profile.fullName || meta.nickName,
            domainElementId: selectedItem.domainElementId,
            metaData: {
              notes: data.notes,
              interferenceInLife: data.isInterference,
              selectedImportance: data.status,
            },
          },
        ],
      });
      setIsSaving(false);
      dispatch(profileActionCreators.fetchDomainTypes(memberId));
      onCloseDrawer();
    } catch (error) {
      setError(error.data.message || error.data.errors[0]?.endUserMessage);
      setIsSaving(false);
    }
  };

  const onSubmitNewReport = payload => {
    const data = {
      patientId: memberId,
      tagItems: payload.map(item => ({
        assignedBy: profile.fullName || meta.nickName,
        domainElementId: item.item.value,
        metaData: {
          interferenceInLife: item.isInterference,
          notes: item.notes,
          selectedImportance: item.status,
        },
      })),
    };
    dispatch(
      profileActionCreators.createNewAssociatedTag({
        data,
        callback: errorMsg => {
          if (!errorMsg) {
            onCloseDrawer();
          }
        },
      }),
    );
  };

  return (
    <>
      <SideEffectDetail
        open={!!selectedItem && !isEditing}
        onClose={onCloseDrawer}
        onClickNewReport={onClickNewReport}
        item={selectedItem}
      />
      <SideEffectEdit
        title="Update side effect"
        isOpen={!!selectedItem && isEditing}
        onClose={onCloseDrawer}
        item={selectedItem}
        onSubmit={onUpdateItem}
        isSaving={isSaving}
        error={errorApi}
      />
      <AddNewReport
        isOpen={showAddItem}
        onClose={onCloseDrawer}
        title="Report new side effect"
        stepTitles={stepTitles}
        showInterference={false}
        searchLabel="Select one or more side effects"
        options={domainOptions}
        onSubmit={onSubmitNewReport}
      />
      <div className={classes.headingWithBtnWrapper}>
        <div className={classes.noteHeaderLeftSide}>
          <Heading className={classes.heading} level={headingLevel.XL} weight={fontWeight.BOLD}>
            Side effects
          </Heading>
          <Badge variant={badgeType.OUTLINED} style={badgeStyle.UNRELATED} className={classes.totalBadge}>
            {domainData?.relatedElements.length} total
          </Badge>
        </div>
        {domainData?.relatedElements.length > 0 && (
          <IconButton
            className={classes.addBtn}
            icon="plus"
            variant={iconBtnType.PRIMARY}
            onClick={onAddNewSideEffects}
          >
            Add side effect
          </IconButton>
        )}
      </div>
      <div className={clsx(classes.noteCardWrapper)}>
        {isLoading ? (
          <Icons className="rotate linear infinite" glyph="in-progress" color={colors.primary} />
        ) : (
          <>
            {domainData?.relatedElements.length ? (
              <>
                {domainData.relatedElements.map((item, idx) => (
                  <SideEffectCard item={item} key={idx} onClick={onSelectedItemClick} />
                ))}
              </>
            ) : (
              <PriorityEmpty
                title={`No Side Effects reported for ${selectedPatient?.member.nickName} yet.`}
                // eslint-disable-next-line max-len
                description="If they've told you about some side effects they're having, please record it here."
                addText="Add side effect"
                onAdd={onAddNewSideEffects}
                glyph="face-talking-confused"
              />
            )}
          </>
        )}
      </div>
    </>
  );
};

export { SideEffects };
